import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import logger from '../../../../app/logger';
import { translate } from '../../../../base/i18n';
import {
    bindAccountMode,
    bindChargeDateData,
    bindDonationAmountIndex,
    bindDonationAmountSelected,
    bindFinalDonationAmount,
    bindFinalDonationAmountError,
    bindFormGUID,
    bindGiventID,
    bindHaslessInstallmentAmt,
    bindInstallmentError,
    bindIsDonationAmountEmpty,
    bindIsDonationDone,
    bindIsEventExpired,
    bindMeetFormSettings,
    bindPaymentError,
    bindPaymentOrderNo,
    bindTouchedFields,
    clearFormApiCache
} from '../../../actions';
import { paymentMethod } from '../styles';
import { fetchFormSettingsCached } from '../utility/apiActions';
import { PAGES } from '../utility/pages';
import { getUserForm } from '../utility/reduxDataUtils';

import AlertMsg from './Alert';
import UserDonationForm from './UserDonationForm';


const UserDonationHome = ({ t, ...props }) => {
    const [ isHavingForm, setIsHavingForm ] = useState(false);
    const [ isPublished, setIsPublished ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(true); // Start with loading true
    const [ formHeader, setFormHeader ] = useState('');
    const [ isEventExpired, setIsEventExpied ] = useState('');
    const [ loadError, setLoadError ] = useState(false);
    const hasLoadedForm = useRef(false);
    const isMountedRef = useRef(true);
    const isFetchInFlight = useRef(false);

    const dispatch = useDispatch();
    const formGuidData = useSelector(
        state => state['features/clickandpledge']?.formGuidData || {}
    );
    const formPublishStatus = useSelector(
        state => state['features/clickandpledge']?.formPublishStatus || false
    );
    const reduxState = useMemo(() => {
        return {
            'features/clickandpledge': {
                formGuidData,
                formPublishStatus
            }
        };
    }, [ formGuidData, formPublishStatus ]);
    const noRecurringOptionAvailable = useSelector(
        state => state['features/clickandpledge']?.noRecurringOptionAvailable || false
    );
    const formGUID = useSelector(
        state => state['features/clickandpledge']?.formGUID || ''
    );
    const giventID = useSelector(
        state => state['features/clickandpledge']?.giventID || 0
    );
    const formSettings = useSelector(
        state => state['features/clickandpledge']?.formSettings || {}
    );
    const showDForm = props.showDForm ?? true;
    const showMareque = props.isloader ?? false;
    const skipAuthentication = props.skipAuthentication ?? false;
    const directFormLoad = props.directFormLoad ?? false;

    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    const setIsLoadingSafe = useCallback(value => {
        if (isMountedRef.current) {
            setIsLoading(value);
        }
    }, []);

    const setLoadErrorSafe = useCallback(value => {
        if (isMountedRef.current) {
            setLoadError(value);
        }
    }, []);

    const setIsHavingFormSafe = useCallback(value => {
        if (isMountedRef.current) {
            setIsHavingForm(value);
        }
    }, []);

    const setIsPublishedSafe = useCallback(value => {
        if (isMountedRef.current) {
            setIsPublished(value);
        }
    }, []);

    const setIsEventExpiedSafe = useCallback(value => {
        if (isMountedRef.current) {
            setIsEventExpied(value);
        }
    }, []);

    const setFormHeaderSafe = useCallback(value => {
        if (isMountedRef.current) {
            setFormHeader(value);
        }
    }, []);

    /**
     * Processes form settings response and updates state/Redux
     * @param {Object} formsettings - Form settings from API response
     */
    const processFormSettings = useCallback(formsettings => {
        const forms = formsettings?.forms || {};
        const fs = forms.FormSettings || {};

        dispatch(bindMeetFormSettings(fs));
        dispatch(bindChargeDateData(forms.ChargeDate));
        dispatch(
            bindAccountMode({
                Mode: forms.Mode,
                accountStatus: forms.Active
            })
        );

        setFormHeaderSafe(fs.FormHeader || '');

        // Check event dates
        const startDate = fs.MeetingStartDate ? new Date(fs.MeetingStartDate) : null;
        const endDate = fs.MeetingEndDate
            ? new Date(fs.MeetingEndDate)
            : null;
        const today = new Date();

        today.setHours(0, 0, 0, 0);

        if (startDate && startDate > today) {
            setIsEventExpiedSafe(t('clickandpledge.eventNotStarted'));
            dispatch(bindIsEventExpired(t('clickandpledge.eventNotStarted')));
        } else if (endDate && endDate < today) {
            setIsEventExpiedSafe(t('clickandpledge.eventExpired'));
            dispatch(bindIsEventExpired(t('clickandpledge.eventExpired')));
        }

        setIsHavingFormSafe(true);
        setIsPublishedSafe(true);
    }, [ dispatch, setFormHeaderSafe, setIsEventExpiedSafe, setIsHavingFormSafe, setIsPublishedSafe, t ]);

    /**
     * Fetches form settings from API with provided guid and giventId
     * @param {string} guid - Form GUID
     * @param {number} giventId - Givent ID
     * @returns {Promise<boolean>} - True if successful, false otherwise
     */
    const fetchAndProcessSettings = useCallback(async (guid, giventId) => {
        try {
            const formsettings = await dispatch(fetchFormSettingsCached({
                guid,
                giventId
            }));

            if (formsettings?.forms?.FormSettings) {
                processFormSettings(formsettings);

                return true;
            }

            setIsHavingFormSafe(false);
            setIsPublishedSafe(false);

            return false;
        } catch (apiError) {
            logger.error('Error fetching form settings:', apiError);
            setIsHavingFormSafe(false);
            setIsPublishedSafe(false);

            return false;
        }
    }, [ dispatch, processFormSettings, setIsHavingFormSafe, setIsPublishedSafe ]);

    const getFormDetails = useCallback(async mData => {
        if (hasLoadedForm.current || isFetchInFlight.current) {
            return;
        }

        hasLoadedForm.current = true;
        isFetchInFlight.current = true;
        setIsLoadingSafe(true);
        setLoadErrorSafe(false);

        try {
            // First check if we have form data in Redux state
            let guid;
            let giventId;

            // For direct form load (non-moderators/non-owners), use formGUID directly without authentication
            if (directFormLoad && skipAuthentication && formGUID) {
                guid = formGUID;
                giventId = giventID || 0;
                await fetchAndProcessSettings(guid, giventId);
            } else if (formGuidData.eId === mData.meetingUUID && formGuidData.guid && formPublishStatus) {
                // If we have form data in Redux and it's published, use it directly
                dispatch(bindGiventID(formGuidData.giventId));
                dispatch(bindFormGUID(formGuidData.guid));

                guid = formGuidData.guid;
                giventId = formGuidData.giventId;
                await fetchAndProcessSettings(guid, giventId);
            } else if (formGUID && giventID) {
                // Use individual formGUID and giventID values from Redux state
                guid = formGUID;
                giventId = giventID;
                await fetchAndProcessSettings(guid, giventId);
            } else {
                // Try to get form data using the Redux utility function
                const res = await getUserForm(reduxState, mData.meetingUUID);

                // Check if we have valid form data
                if (res.data?.body?.Items?.length > 0 && res.data.body.Items[0]?.Attributes?.[3]?.Value) {
                    guid = res.data.body.Items[0].Attributes[3].Value;
                    giventId = res.data.body.Items[0].Attributes[5].Value;

                    dispatch(bindGiventID(giventId));
                    dispatch(bindFormGUID(guid));

                    await fetchAndProcessSettings(guid, giventId);
                } else {
                    // No form items found - this is expected for guests before host publishes
                    setIsHavingFormSafe(false);
                    setIsPublishedSafe(false);
                }
            }
        } catch (err) {
            logger.error('getFormDetails error:', err);
            setIsHavingFormSafe(false);
            setLoadErrorSafe(true);
            hasLoadedForm.current = false; // Allow retry on error
        } finally {
            setIsLoadingSafe(false);
            isFetchInFlight.current = false;
        }
    }, [
        directFormLoad,
        fetchAndProcessSettings,
        formGUID,
        formGuidData,
        formPublishStatus,
        giventID,
        setIsHavingFormSafe,
        setIsLoadingSafe,
        setIsPublishedSafe,
        setLoadErrorSafe,
        skipAuthentication
    ]);

    const handleRefresh = useCallback(async () => {
        // Reset essential transactional state to start over
        dispatch(bindIsDonationDone(false));
        dispatch(bindPaymentOrderNo(''));
        dispatch(bindDonationAmountSelected(''));
        dispatch(bindDonationAmountIndex(null));
        dispatch(bindFinalDonationAmount(''));
        dispatch(bindInstallmentError(false));
        dispatch(bindHaslessInstallmentAmt(false));
        dispatch(bindFinalDonationAmountError(false));
        dispatch(bindIsDonationAmountEmpty(false));
        dispatch(bindPaymentError(false));
        dispatch(bindTouchedFields([]));

        // Clear form-related API cache so data is re-fetched fresh
        // (preserves registration cache — no re-registration)
        dispatch(clearFormApiCache());

        // Clear boost state on manual refresh
        sessionStorage.removeItem('seedAmount');
        sessionStorage.removeItem('currentBoostStep');
        sessionStorage.removeItem('boostClicked');

        // Return to the first page and show the loader
        props.onNavigate(PAGES.LOAD_FORM);
        setIsLoading(true);
        setIsHavingForm(false);

        // Reset the form loading flag to force a fresh data fetch
        hasLoadedForm.current = false;
        isFetchInFlight.current = false;
        if (props.meetingData) {
            await getFormDetails(props.meetingData);
        }
    }, [ dispatch, props.onNavigate, props.meetingData, getFormDetails ]);

    useEffect(() => {
        // Only fetch form details once when component mounts with valid data
        if (props.meetingData && props.userData && !hasLoadedForm.current) {
            getFormDetails(props.meetingData);
        }

        if (showMareque) {
            setIsLoadingSafe(false);
        }
    }, [
        props.meetingData,
        props.userData,
        props.showDForm,
        showMareque,
        directFormLoad,
        skipAuthentication,
        formGUID,
        giventID,
        getFormDetails,
        setIsLoadingSafe
    ]); // Added formGUID and giventID to dependencies

    // Additional useEffect to handle when formGUID becomes available for non-moderators
    useEffect(() => {
        // If we're in directFormLoad mode and formGUID just became available, try to load the form
        if (directFormLoad && skipAuthentication && formGUID && props.meetingData && props.userData && !hasLoadedForm.current) {
            getFormDetails(props.meetingData);
        }
    }, [ formGUID, giventID, directFormLoad, skipAuthentication, props.meetingData, props.userData, getFormDetails ]);

    const showdonationform = () => {
        hasLoadedForm.current = false; // Reset to allow reload
        if (props.meetingData && props.userData) {
            // If host and no form data exists, redirect to setup
            if (props.ishost && !formGuidData.guid) {
                // Navigate back to auto register to start the setup process
                props.onNavigate && props.onNavigate(PAGES.AUTO_REGISTER);

                return;
            }
            getFormDetails(props.meetingData);
        }
    };

    const st_1 = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%'
    };

    return (
        <div className = 'recurring_transaction_container'>
            <div className = 'recurring_transaction_wrapper'>
                <div
                    className = 'recurring_transaction_card'
                    id = 'recurring_main_tabs'>
                    <div className = 'recurring_transaction_card_body'>
                        <div className = 'recurring_transaction_main_form'>
                            <div className = 'recurring_transaction_tab_content active'>
                                {!props.hideHeader && (<div
                                    className = 'transaction_info_logo_container'
                                    style = { st_1 }>
                                    {(formHeader || formSettings?.FormHeader) && !noRecurringOptionAvailable
                                        && <div className = 'header-name'>{formHeader || formSettings?.FormHeader}</div>
                                    }
                                    <button
                                        className = { `refresh-campaign-btn${isLoading ? ' refreshing' : ''}` }
                                        disabled = { isLoading }
                                        onClick = { handleRefresh }
                                        style = { paymentMethod.refreshButton }
                                        title = { t('clickandpledge.refreshCampaign') }
                                        type = 'button'>
                                        <svg
                                            className = { isLoading ? 'spin-animation' : '' }
                                            fill = 'none'
                                            height = '18'
                                            stroke = 'currentColor'
                                            strokeLinecap = 'round'
                                            strokeLinejoin = 'round'
                                            strokeWidth = '2'
                                            viewBox = '0 0 24 24'
                                            width = '18'>
                                            <path d = 'M23 4v6h-6' />
                                            <path d = 'M1 20v-6h6' />
                                            <path d = 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15' />
                                        </svg>
                                    </button>
                                </div>)}
                                <div
                                    className = 'transaction_info_wrapper cnp_table transaction-info-column'>
                                    {isLoading
                                        ? (
                                            <div className = 'loader-container'>
                                                <div className = 'lds-ellipsis'>
                                                    <div />
                                                    <div />
                                                    <div />
                                                    <div />
                                                </div>
                                            </div>)
                                        : isHavingForm ? (
                                            <UserDonationForm
                                                directFormLoad = { directFormLoad }
                                                isHost = { props.ishost }
                                                meetingData = { props.meetingData }
                                                onNavigate = { props.onNavigate }
                                                requireContactInfo = { props.requireContactInfo }
                                                showDForm = { showDForm }
                                                skipAuthentication = { skipAuthentication }
                                                userData = { props.userData } />
                                        ) : props.ishost ? (
                                            <>
                                                <AlertMsg
                                                    isClose = { false }
                                                    message = { loadError
                                                        ? t('clickandpledge.loadFormError')
                                                        : t('clickandpledge.noFormFound') }
                                                    type = { loadError ? 'failure' : 'info' } />
                                                <div className = 'form_input_wrapper d-flex justify-content-between mt_2'>
                                                    <button
                                                        className = 'full_button tabindex_btn next setup-form-button'
                                                        onClick = { showdonationform }
                                                        type = 'button'>
                                                        {loadError ? t('clickandpledge.retry') : t('clickandpledge.setupForm')}
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {isPublished && (
                                                    <AlertMsg
                                                        isClose = { false }
                                                        message = { t('clickandpledge.clickLoadForm') }
                                                        type = 'success' />
                                                )}
                                                {!isPublished && (
                                                    <AlertMsg
                                                        isClose = { true }
                                                        message = { t('clickandpledge.hostBroadcastingSoon') }
                                                        type = 'failure' />
                                                )}
                                                {isEventExpired && (
                                                    <AlertMsg
                                                        isClose = { false }
                                                        message = { isEventExpired }
                                                        type = 'failure' />
                                                )}
                                                <div className = 'form_input_wrapper d-flex justify-content-between mt_2'>
                                                    <button
                                                        className = 'full_button tabindex_btn next'
                                                        disabled = { Boolean(isEventExpired) }
                                                        onClick = { showdonationform }
                                                        type = 'button'>
                                                        {t('clickandpledge.retry')}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default translate(UserDonationHome);
